import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React, {ChangeEvent, forwardRef, Fragment, useEffect, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useIMask} from 'react-imask';

import {Tooltip, tooltipClasses, useTheme} from '@mui/material';
import Icon from 'presentation/icons';
import {Loading} from '../Loading/Loading';
import {MaskedInputProps} from './types';

export const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>((props, ref) => {
	const {
		maskOptions,
		onChange,
		handleAction,
		handleClearQuery,
		endAdornmentIcon,
		endAdornmentVariant = 'close',
		isPriceValue,
		isRefundFlow,
		tooltipText,
		dataTagId,
		...rest
	} = props;
	const {palette} = useTheme();
	const [isFocus, setIsFocus] = useState(false);
	const {resetField, getValues} = useFormContext();
	const currentValue = getValues(props.$fieldName);

	const {
		ref: IMaskInput,
		setValue,
		value,
	} = useIMask(maskOptions, {
		onAccept(_value, _maskRef, e?) {
			if (e) {
				onChange?.(e as unknown as ChangeEvent<HTMLInputElement>);
			} else {
				onChange?.({target: {value: _value}} as unknown as ChangeEvent<HTMLInputElement>);
			}
		},
	});

	function handleClear() {
		resetField(props.$fieldName);
		setValue('');
		if (handleClearQuery) {
			handleClearQuery();
		}
	}

	function handleOnFocus() {
		if (isRefundFlow) {
			setValue('');
		}
		setIsFocus(true);
		if (props.onFocus) {
			props.onFocus();
		}
	}

	useEffect(() => {
		setValue(currentValue ?? '');
	}, [currentValue]);

	const inputAdornment = () => {
		if (endAdornmentVariant === 'close') {
			return (
				<Fragment>
					{value ? (
						<InputAdornment
							sx={{
								display: isRefundFlow && !isFocus ? 'none' : 'inline-flex',
							}}
							position="end">
							<IconButton aria-label="clear field" onClick={handleClear} edge="end">
								<Icon name="close-filled" />
							</IconButton>
						</InputAdornment>
					) : null}
				</Fragment>
			);
		}

		if (endAdornmentVariant === 'loading') {
			return <Loading boxProps={{sx: {alignItems: 'end'}}} size={20} color="primary" />;
		}

		if (endAdornmentVariant === 'tooltip') {
			return (
				<Tooltip
					slotProps={{
						popper: {
							sx: {
								[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
									{
										marginTop: '0px',
										backgroundColor: palette.other.snackbarBackground,
										padding: '16px 12px',
										fontSize: '1rem',
									},
								[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.arrow}`]:
									{
										color: palette.other.snackbarBackground,
									},
							},
						},
					}}
					placement="bottom-start"
					arrow
					title={tooltipText}>
					<IconButton
						sx={{cursor: 'default'}}
						disableRipple
						disableTouchRipple
						disableFocusRipple>
						<Icon name="help-outline-filled" size={20} color={palette.primary.main} />
					</IconButton>
				</Tooltip>
			);
		}
		return (
			<Fragment>
				{endAdornmentIcon ? (
					<InputAdornment position="end">
						<IconButton onClick={handleAction} edge="end" data-tag-id={dataTagId}>
							<Icon name={endAdornmentIcon} />
						</IconButton>
					</InputAdornment>
				) : null}
			</Fragment>
		);
	};

	return (
		<TextField
			{...rest}
			value={value}
			ref={ref}
			inputRef={IMaskInput}
			onBlur={() => setIsFocus(false)}
			onFocus={handleOnFocus}
			slotProps={{
				...rest.slotProps,
				input: {
					...rest.slotProps?.input,
					endAdornment: inputAdornment(),
					startAdornment:
						isPriceValue && value ? (
							<InputAdornment position="start">R$ </InputAdornment>
						) : null,
				},
			}}
		/>
	);
});
